import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/api';

import {
	SuperAdminContainer,
	CenterCard,
	Title,
	Subtitle,
	Form,
	InputGroup,
	InputLabel,
	Star,
	ErrorPopup,
	CloseButton,
	EyeIcon,
	CheckboxGroup,
	CheckboxLabel,
	Divider,
	GlobalStyle,
} from '../style-comps/SuperAdminLoginPage.styles';

import Input from '../components/Input';
import Button from '../components/Button';
import Checkbox from '../components/Checkbox';
import SupportContactModal from '../components/SupportContactModal';

import LogoImg from '../Assets/LogoImg01.png';

// ── Helpers ────────────────────────────────────────────────────────────────
const formatPasswordDate = (isoString) => {
	if (!isoString) return '';
	let dateString = isoString;
	if (isoString.includes('T') && !isoString.includes('Z') && !isoString.includes('+')) {
		dateString = isoString + 'Z';
	}
	const date = new Date(dateString);
	const formatter = new Intl.DateTimeFormat('en-AU', {
		timeZone: 'Australia/Sydney',
		year: 'numeric', month: '2-digit', day: '2-digit',
		hour: '2-digit', minute: '2-digit', hour12: false,
	});
	const parts = formatter.formatToParts(date);
	const v = (t) => parts.find(p => p.type === t)?.value;
	return `${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')} (Sydney Time)`;
};

// ── Component ──────────────────────────────────────────────────────────────
const SuperAdminLoginPage = ({ onLogin }) => {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showError, setShowError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [failCount, setFailCount] = useState(0);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [showSupportModal, setShowSupportModal] = useState(false);

	const navigate = useNavigate();
	const passwordRef = useRef(null);

	// ── Remember Me ────────────────────────────────────────────────────
	useEffect(() => {
		const saved = localStorage.getItem('superAdminRememberMe');
		const savedUsername = localStorage.getItem('superAdminSavedUsername');
		if (saved === 'true' && savedUsername) {
			setUsername(savedUsername);
			setRememberMe(true);
			passwordRef.current?.focus();
		}
	}, []);

	// ── Login handler ──────────────────────────────────────────────────
	const handleLogin = async (e) => {
		e.preventDefault();

		try {
			const data = await login(username, password);

			if (data?.Token) {
				// 用 API 回傳的 Roles 直接判斷（localStorage 此時尚未寫入）
				const roles = data.Roles || [];
				const isSuperAdmin = roles.some(r =>
					r.toLowerCase() === 'superadmin' || r.toLowerCase() === 'super_admin'
				);

				if (!isSuperAdmin) {
					setErrorMessage('Access denied. This portal is for Super Admin accounts only.');
					setShowError(true);
					return;
				}

				// Remember Me
				if (rememberMe) {
					localStorage.setItem('superAdminRememberMe', 'true');
					localStorage.setItem('superAdminSavedUsername', username);
				} else {
					localStorage.removeItem('superAdminRememberMe');
					localStorage.removeItem('superAdminSavedUsername');
				}

				// Clear previous user cache
				const allKeys = Object.keys(localStorage);
				allKeys.forEach(key => {
					if (
						key.startsWith('dashboard_reports_') ||
						key.startsWith('cachedUserListings') ||
						key.startsWith('cachedListingInfoList') ||
						key.startsWith('cachedCalendarData') ||
						key.startsWith('cachedReservationMap') ||
						key.startsWith('calendarCacheTime') ||
						key.startsWith('reportViewed-') ||
						key.startsWith('userData')
					) {
						localStorage.removeItem(key);
					}
				});

				onLogin?.();

				const requireChange = data.RequirePasswordChange === true;
				if (requireChange) {
					navigate('/dashboard/setting');
				} else {
					navigate('/dashboard');
				}
			} else {
				setErrorMessage('Login failed. Please try again.');
				setShowError(true);
			}
		} catch (error) {
			console.group('🔴 SuperAdmin Login Error');
			console.error('Error:', error);
			console.error('Response:', error.response);
			console.groupEnd();

			let errorMsg = 'An unexpected error occurred';

			if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
				errorMsg = 'Unable to connect to server. Please check your internet connection.';
			} else if (!error.response) {
				errorMsg = 'Server is not responding. Please try again later.';
			} else {
				const { data, status } = error.response;
				switch (status) {
					case 401: {
						const savedPwDate = localStorage.getItem('passwordUpdatedAt');
						let msg = data?.message || 'Invalid email or password';
						if (savedPwDate) {
							msg += `\nYour password was last updated on ${formatPasswordDate(savedPwDate)}.`;
						}
						errorMsg = msg;
						break;
					}
					case 403:
						errorMsg = data?.message || 'Access denied. Your account may not be authorized.';
						break;
					case 500:
						errorMsg = 'Server error. Please try again later.';
						break;
					default:
						errorMsg =
							typeof data === 'string'
								? data
								: data?.message || data?.error || `Error: ${status}`;
				}
			}

			setErrorMessage(errorMsg);
			const newFailCount = failCount + 1;
			setFailCount(newFailCount);
			if (newFailCount >= 2) setShowForgotPassword(true);
			setShowError(true);
		}
	};

	const handleForgotPassword = () => {
		setShowSupportModal(true);
	};

	// ── Render ─────────────────────────────────────────────────────────
	return (
		<>
			<GlobalStyle />

			<SuperAdminContainer>
				<CenterCard>
					{/* Logo */}
					<img
						src={LogoImg}
						alt="KozyGuru"
						style={{ width: '160px', height: 'auto', marginBottom: '12px' }}
					/>

					{/* Heading */}
					<Title>Hi , SuperAdmin</Title>
					<Subtitle>Please log in to continue</Subtitle>

					{/* Form */}
					<Form onSubmit={handleLogin}>
						{/* Email */}
						<InputGroup>
							<InputLabel htmlFor="sa-email">Email</InputLabel>
							<Input
								id="sa-email"
								type="email"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								placeholder="Input text"
								autoCapitalize="off"
								required
							/>
						</InputGroup>

						{/* Password */}
						<InputGroup>
							<InputLabel htmlFor="sa-password">
								Passoword<Star>*</Star>
							</InputLabel>
							<div style={{ position: 'relative', width: '100%' }}>
								<Input
									id="sa-password"
									ref={passwordRef}
									type={showPassword ? 'text' : 'password'}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="••••••••"
									style={{ paddingRight: '44px' }}
									required
								/>
								<EyeIcon
									src={
										showPassword
											? '/Assets/Eyes-open.png'
											: '/Assets/Eyes-close.png'
									}
									alt="Toggle visibility"
									onClick={() => setShowPassword(!showPassword)}
								/>
							</div>
						</InputGroup>

						{/* Forgot Password — appears after 2 failed attempts */}
						{showForgotPassword && (
							<div style={{ textAlign: 'right', marginBottom: '8px' }}>
								<span
									onClick={handleForgotPassword}
									style={{
										color: '#61cbc9',
										fontSize: '13px',
										cursor: 'pointer',
										textDecoration: 'underline',
									}}
								>
									Forgot password? Contact Support
								</span>
							</div>
						)}

						{/* Remember me + Login button */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '12px',
								marginBottom: '4px',
							}}
						>
							<CheckboxGroup style={{ marginBottom: 0 }}>
								<Checkbox
									isChecked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
								/>
								<CheckboxLabel>Remember me</CheckboxLabel>
							</CheckboxGroup>

							<Button
								label="Login"
								style={{
									backgroundColor: '#61cbc9',
									color: '#ffffff',
									padding: '12px 36px',
									fontSize: '16px',
									fontWeight: '600',
									border: 'none',
									borderRadius: '8px',
									cursor: 'pointer',
									whiteSpace: 'nowrap',
								}}
							/>
						</div>
					</Form>

					<Divider />
				</CenterCard>
			</SuperAdminContainer>

			{/* Error popup */}
			{showError && (
				<ErrorPopup show={showError}>
					<div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>
					<CloseButton onClick={() => setShowError(false)}>×</CloseButton>
				</ErrorPopup>
			)}

			{/* Forgot password / contact support */}
			<SupportContactModal
				isOpen={showSupportModal}
				onClose={() => setShowSupportModal(false)}
				accountEmail={username}
				isSuperAdmin
			/>
		</>
	);
};

export default SuperAdminLoginPage;
